import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  authService,
  isAttachmentReady,
  projectService,
  readAttachmentStatus,
  selectUser,
  setDesignSystemExtracting,
  setShowOnboarding,
  useAppDispatch,
  useAppSelector,
  useInvalidateStartups,
} from '@ideoz/core';
import {
  Button,
  INDUSTRIES,
  IndustryMultiSelect,
  Input,
  Label,
  RichTextEditor,
  showToast,
  cn,
  type RichTextEditorRef,
} from '@ideoz/ui';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  CloudUpload,
  FileText as FileIcon,
  Image as ImageIcon,
  Loader2,
  Sparkles,
  X,
  type LucideIcon,
} from 'lucide-react';
import { IdeozLogo } from '@ideoz/ui/components/onboarding/components';

/** Poll every 5s. Faster than 60s so users don't sit staring at the loader
 * after processing already finished; slower than 1–2s so we don't hammer Nest. */
const PREPARE_POLL_MS = 5_000;
/** After 3 minutes of polling we give up waiting and let the user in anyway —
 * files continue processing in the background. */
const PREPARE_MAX_WAIT_MS = 3 * 60 * 1000;

/** The four onboarding steps, with the copy shown in the right-hand stepper. */
const STEP_META = [
  {
    id: 1,
    label: 'Startup name',
    desc: 'This is how your workspace and every artifact will be labelled.',
  },
  {
    id: 2,
    label: 'Startup details',
    desc: 'The more context you give, the sharper your product brain gets.',
  },
  {
    id: 3,
    label: 'Product details',
    desc: 'Ideoz learns your tone of voice and design system from what you attach.',
  },
  {
    id: 4,
    label: 'Startup files',
    desc: 'Research, decks, transcripts — optional, but they make answers better.',
  },
] as const;

interface FormState {
  name: string;
  industry: string;
  target_icp: string;
  problem_details: string;
  /** Step 3 — free-text tone-of-voice source files (.md / .csv / .xls / .xlsx).
   * Uploaded on submit with `extract_tone=true` so the AI-API can learn tone of voice. */
  content_source_files: File[];
  /** Step 3 — product screenshots. On submit these get sent through the
   * existing design-system extraction pipeline (fire-and-forget) so the
   * generated DS is pre-populated for the new startup. */
  screenshot_files: File[];
  /** Step 4 — general startup files. */
  files: File[];
}

const INITIAL: FormState = {
  name: '',
  industry: '',
  target_icp: '',
  problem_details: '',
  content_source_files: [],
  screenshot_files: [],
  files: [],
};

/** Accepted MIME/extension patterns for the content-source dropzone. */
const CONTENT_SOURCE_ACCEPT =
  '.md,.markdown,.csv,.xls,.xlsx,text/markdown,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
/** Accepted MIME/extension patterns for the product-screenshots dropzone. */
const SCREENSHOT_ACCEPT = 'image/png,image/jpeg,image/jpg,image/webp';

/**
 * Keys the Nest `UpdateDesignSystemDto` will accept. The `/design-system/extract`
 * endpoint returns extra scaffolding (notably a top-level `reasoning` string
 * with the model's pixel-level narration) that Nest rejects with a 400 under
 * `forbidNonWhitelisted: true`. We filter to this whitelist before PATCHing.
 * Keep in sync with `UpdateDesignSystemDto` on the backend.
 */
const DESIGN_SYSTEM_DTO_KEYS = [
  'primaryColor',
  'primaryForeground',
  'secondaryColor',
  'secondaryForeground',
  'fontFamily',
  'headingFontFamily',
  'theme',
  'appBg',
  'surfaceColor',
  'borderColor',
  'mutedTextColor',
  'successColor',
  'warningColor',
  'errorColor',
  'infoColor',
  'borderRadius',
  'shadows',
  'density',
  'elevationStyle',
  'buttonStyle',
  'iconStyle',
  'inputStyle',
  'uxPatterns',
] as const;

export default function Onboarding(): JSX.Element {
  const dispatch = useAppDispatch();
  const invalidateStartups = useInvalidateStartups();
  const navigate = useNavigate();
  const user = useAppSelector(selectUser);

  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
  const [form, setForm] = useState<FormState>(INITIAL);
  const [isSubmitting, setIsSubmitting] = useState(false);
  /** Set after createStartup + all upload requests fire. While non-null we render
   * the "Preparing your startup" screen and poll attachments status until every
   * uploaded file is ready — then we navigate. `awaitingDesignSystem` is a
   * cosmetic hint used only to swap the copy on the loading screen so users
   * who uploaded screenshots know their design system is being extracted in
   * the background. */
  const [preparingStartup, setPreparingStartup] = useState<{
    projectId: string;
    slug: string;
    expectedFileCount: number;
    awaitingDesignSystem: boolean;
  } | null>(null);
  const problemEditorRef = useRef<RichTextEditorRef>(null);

  const userFirstName = (user?.name ?? '').split(' ')[0] || 'there';
  const nextDisabled = step === 1 && !form.name.trim();

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const isEmptyRichText = (html: string) =>
    !html || !html.replace(/<[^>]+>/g, '').replace(/&nbsp;|\s+/g, '').length;

  const goNext = () => setStep((s) => (s < 4 ? ((s + 1) as 1 | 2 | 3 | 4) : s));
  const goBack = () => setStep((s) => (s > 1 ? ((s - 1) as 1 | 2 | 3 | 4) : s));

  const handleSubmit = async () => {
    if (isSubmitting || !form.name.trim()) return;
    setIsSubmitting(true);
    try {
      const rawProblem = problemEditorRef.current?.getContent() ?? form.problem_details;
      const problemDetails = isEmptyRichText(rawProblem) ? '' : rawProblem;
      const startup = await projectService.createStartup({
        name: form.name.trim(),
        ...(form.industry && { industry: form.industry }),
        ...(form.target_icp && { target_icp: form.target_icp }),
        ...(problemDetails && { problem_details: problemDetails }),
      });

      // Fire upload requests for all three attachment categories collected
      // during onboarding: content-source (tone), general startup files, and
      // (later) screenshots which take a different path via the design-system
      // extractor. Each POST returns once the AI-API has queued the file for
      // processing — actual chunking/embedding continues async on the server.
      // We poll attachments status below to know when all are ready.
      // Individual upload failures don't block the flow; they just get logged
      // and the file won't appear in the ready list.
      //
      const uploadOnboardingFile = async (file: File, extractTone = false) => {
        const fd = new FormData();
        fd.append('file', file);
        await projectService.uploadProjectFiles(startup.id, fd, { extractTone });
      };

      let uploadedCount = 0;
      for (const file of form.content_source_files) {
        try {
          await uploadOnboardingFile(file, true);
          uploadedCount += 1;
        } catch (err) {
          console.error('Failed to upload onboarding tone file', file.name, err);
        }
      }
      for (const file of form.files) {
        try {
          await uploadOnboardingFile(file);
          uploadedCount += 1;
        } catch (err) {
          console.error('Failed to upload onboarding file', file.name, err);
        }
      }

      // Product screenshots → design-system extraction. Fire-and-forget: the
      // Gemini vision call takes 20-60s and we don't want to block navigation.
      // When it resolves we save the extracted tokens onto the project's
      // design system row so the user lands on their startup with brand
      // colors + interaction patterns pre-populated. If nothing is attached
      // this just no-ops.
      const willExtractDesignSystem = form.screenshot_files.length > 0;
      if (willExtractDesignSystem) {
        // Flag the startup as "extracting DS" so the DesignSystemPanel shows
        // a loader (not stale defaults) if the user navigates to
        // /design-system before this fire-and-forget call finishes.
        dispatch(setDesignSystemExtracting({ projectId: startup.id, extracting: true }));
        void (async () => {
          try {
            const extracted = await projectService.extractDesignSystem(form.screenshot_files);
            // Two filters before PATCHing:
            //   1. Drop null/undefined so we never overwrite existing values.
            //   2. Whitelist to `DESIGN_SYSTEM_DTO_KEYS` so extractor-only
            //      fields (e.g. `reasoning`) don't trigger 400 from Nest's
            //      `forbidNonWhitelisted: true` validation.
            const payload = Object.fromEntries(
              (DESIGN_SYSTEM_DTO_KEYS as readonly string[])
                .filter((key) => {
                  const value = (extracted as Record<string, unknown> | null | undefined)?.[key];
                  return value !== null && value !== undefined;
                })
                .map((key) => [key, (extracted as Record<string, unknown>)[key]])
            );
            if (Object.keys(payload).length > 0) {
              await projectService.updateDesignSystem(startup.id, payload);
              showToast({
                message: 'Your design system was applied from your screenshots.',
                severity: 'success',
              });
            }
          } catch (err) {
            console.error('Failed to auto-extract design system from onboarding screenshots', err);
            showToast({
              message:
                'Could not build design system from your screenshots — you can add it later.',
              severity: 'error',
            });
          } finally {
            dispatch(setDesignSystemExtracting({ projectId: startup.id, extracting: false }));
          }
        })();
      }

      try {
        await authService.updateOnboardingShown();
      } catch (err) {
        console.error('Failed to mark onboarding shown:', err);
      }
      dispatch(setShowOnboarding(false));
      await invalidateStartups();

      // No files → nothing to wait for, land on Work Scopes immediately.
      if (uploadedCount === 0) {
        navigate(`/${startup.slug}/work-scopes`);
        return;
      }

      // Otherwise switch to the "Preparing your startup" screen and let the
      // effect below poll until every file's processing_status === READY.
      setPreparingStartup({
        projectId: startup.id,
        slug: startup.slug,
        expectedFileCount: uploadedCount,
        awaitingDesignSystem: willExtractDesignSystem,
      });
    } catch (err) {
      console.error('Failed to create startup:', err);
      showToast({ message: 'Failed to create startup. Please try again.', severity: 'error' });
      setIsSubmitting(false);
    }
  };

  // Polling loop for the "Preparing your startup" phase. Runs only while
  // preparingStartup is non-null. Polls every PREPARE_POLL_MS; if all uploaded
  // files reach READY we navigate straight to the startup home. If we hit
  // PREPARE_MAX_WAIT_MS without all files ready we navigate anyway — the
  // remaining files keep processing on the server, they'll show up when done.
  useEffect(() => {
    if (!preparingStartup) return;
    const startedAt = Date.now();
    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const check = async () => {
      if (cancelled) return;
      try {
        const attachments = (await projectService.getProjectAttachments(
          preparingStartup.projectId
        )) as Array<{ processingStatus?: string | null }> | null | undefined;
        const list = Array.isArray(attachments) ? attachments : [];
        const readyCount = list.filter((a) => isAttachmentReady(readAttachmentStatus(a))).length;
        const allReady =
          list.length >= preparingStartup.expectedFileCount &&
          readyCount >= preparingStartup.expectedFileCount;
        if (allReady) {
          navigate(`/${preparingStartup.slug}/work-scopes`);
          return;
        }
      } catch (err) {
        // Transient errors just retry — a single failed poll shouldn't strand
        // the user on the loading screen.
        console.error('Failed to check attachment processing status', err);
      }

      if (Date.now() - startedAt >= PREPARE_MAX_WAIT_MS) {
        showToast({
          message: 'Some files are still processing — they will appear shortly.',
          severity: 'info',
        });
        navigate(`/${preparingStartup.slug}/work-scopes`);
        return;
      }

      if (!cancelled) {
        timeoutId = setTimeout(check, PREPARE_POLL_MS);
      }
    };

    check();

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [preparingStartup, navigate]);

  // "Preparing your startup" is a blocking full-screen state — no back button,
  // no nav, no way for the user to escape until every uploaded file is
  // processed (or we time out). Rendered while:
  //   - handleSubmit is mid-flight (isSubmitting && !preparingStartup)
  //   - or we're polling for file readiness (preparingStartup != null)
  if (isSubmitting || preparingStartup) {
    const isPreparingFiles = preparingStartup !== null;
    const awaitingDesignSystem = preparingStartup?.awaitingDesignSystem === true;
    return (
      <div className="min-h-screen bg-background px-12 py-8 flex flex-col">
        <BrandLogo />
        <div className="flex-1 flex flex-col items-center justify-center gap-4 text-center">
          <Loader2 className="w-10 h-10 text-blue-500 animate-spin" />
          <h2 className="text-lg font-semibold text-foreground">
            {isPreparingFiles ? 'Preparing your startup' : 'Loading your startup profile'}
          </h2>
          <p className="text-sm text-muted-foreground max-w-md">
            {isPreparingFiles
              ? `We're getting your files ready. This usually takes less than a minute. Please keep this tab open.`
              : `Creating your startup — hang tight.`}
          </p>
          {awaitingDesignSystem && (
            <p className="text-sm text-blue-500 max-w-md">
              We're also building your design system from the screenshots you uploaded. This
              continues in the background — you can start using your startup as soon as the files
              finish.
            </p>
          )}
        </div>
      </div>
    );
  }

  // `form.industry` is a comma-separated list of selected values; map each back
  // to its display label for the workspace preview on the right.
  const industryLabel = form.industry
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean)
    .map((v) => INDUSTRIES.find((o) => o.value === v)?.label ?? v)
    .join(', ');

  return (
    <div className="flex h-[100vh] bg-background">
      {/* Left — form */}
      <div className="flex w-full flex-col overflow-y-auto px-8 py-10 lg:w-[52%] lg:px-20 lg:py-14">
        <BrandLogo />

        <div className="mx-auto mt-14 w-full max-w-xl">
          <p className="text-sm font-medium text-blue-600 dark:text-blue-400">
            Hi {userFirstName},
          </p>
          <h1 className="mt-2 text-4xl font-bold text-foreground">{stepHeadline(step)}</h1>
          <p className="mt-3 text-base text-muted-foreground">{stepSubtitle(step)}</p>

          <div className="mt-10">
            {step === 1 && (
              <FieldRow label="Your startup name">
                <Input
                  id="onb-name"
                  autoFocus
                  value={form.name}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setField('name', e.target.value)
                  }
                  placeholder="example: Ideoz"
                />
              </FieldRow>
            )}

            {step === 2 && (
              <>
                <FieldRow label="Please select your industry" optional>
                  <IndustryMultiSelect
                    value={form.industry}
                    onChange={(v) => setField('industry', v)}
                  />
                </FieldRow>

                <FieldRow label="Target users / ICP" optional>
                  <Input
                    value={form.target_icp}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setField('target_icp', e.target.value)
                    }
                    placeholder="Who are your users? Write freely."
                  />
                </FieldRow>

                <FieldRow label="What problem are you solving?" optional>
                  <div className="min-h-[120px]">
                    <RichTextEditor
                      ref={problemEditorRef}
                      initialValue={form.problem_details}
                      placeholder="What problem are you solving? What are you working on?"
                      onChange={(content) => setField('problem_details', content)}
                    />
                  </div>
                </FieldRow>
              </>
            )}

            {step === 3 && (
              <>
                <FieldRow label="Attach content source file" optional>
                  <FileDropZone
                    files={form.content_source_files}
                    accept={CONTENT_SOURCE_ACCEPT}
                    icon={FileIcon}
                    hint="Markdown, CSV or Excel — used by the AI to learn your product's tone of voice."
                    onChange={(files) => setField('content_source_files', files)}
                  />
                </FieldRow>

                <FieldRow label="Attach product screenshots" optional>
                  <FileDropZone
                    files={form.screenshot_files}
                    accept={SCREENSHOT_ACCEPT}
                    icon={ImageIcon}
                    hint="PNG / JPEG / WEBP — used to auto-extract your design system on submit."
                    onChange={(files) => setField('screenshot_files', files)}
                  />
                </FieldRow>
              </>
            )}

            {step === 4 && (
              <FieldRow label="Attach files" optional>
                <FileDropZone
                  files={form.files}
                  icon={CloudUpload}
                  hint="Decks, research, transcripts — anything related to your startup or product."
                  onChange={(files) => setField('files', files)}
                />
              </FieldRow>
            )}
          </div>
        </div>

        {/* Bottom bar: Back (left) + Next / Create (right) */}
        <div className="mx-auto mt-auto flex w-full max-w-xl items-center justify-between pt-12">
          {step > 1 ? (
            <Button
              variant="secondary-gray"
              size="lg"
              onClick={goBack}
              className="gap-2"
              data-testid="onboarding-back"
            >
              <ArrowLeft size={16} />
              Back
            </Button>
          ) : (
            <span />
          )}
          {step < 4 ? (
            <Button
              size="lg"
              onClick={goNext}
              disabled={nextDisabled}
              className="gap-2 border-transparent bg-foreground text-background hover:bg-foreground/90"
              data-testid="onboarding-next"
            >
              Next
              <ArrowRight size={16} />
            </Button>
          ) : (
            <Button
              size="lg"
              onClick={handleSubmit}
              disabled={!form.name.trim()}
              className="gap-2 border-transparent bg-foreground text-background hover:bg-foreground/90"
              data-testid="onboarding-create"
            >
              <Sparkles size={16} />
              Create my startup
            </Button>
          )}
        </div>
      </div>

      {/* Right — progress + context */}
      <aside className="hidden flex-col justify-between overflow-y-auto border-l border-border bg-surface-2 px-12 py-14 lg:flex lg:w-[48%]">
        <Stepper currentStep={step} />

        <div className="mt-10">
          <WhyThisMatters
            text={whyThisMatters(step)}
            name={form.name}
            industryLabel={industryLabel}
          />
        </div>
      </aside>
    </div>
  );
}

function stepHeadline(step: 1 | 2 | 3 | 4): string {
  switch (step) {
    case 1:
      return "Let's start with a name";
    case 2:
      return 'Tell us about your startup';
    case 3:
      return 'Now, your product';
    case 4:
      return 'Drop anything else';
  }
}

function stepSubtitle(step: 1 | 2 | 3 | 4): string {
  return STEP_META[step - 1].desc;
}

function whyThisMatters(step: 1 | 2 | 3 | 4): string {
  switch (step) {
    case 1:
      return 'Everything here is optional except your startup name — you can refine it later.';
    case 2:
      return 'Target users and the problem statement drive how Ideoz frames insights.';
    case 3:
      return 'Screenshots let Ideoz auto-extract colors, type and spacing into a design system.';
    case 4:
      return 'Uploaded files become searchable context for every scope you run.';
  }
}

/** The "id." wordmark — shrunk and recoloured to follow the theme (SVG fills are
 * overridden via `fill-current`, size via the logo's root div). */
function BrandLogo(): JSX.Element {
  return (
    <span className="text-foreground [&>div]:!h-7 [&>div]:!w-[40px] [&_ellipse]:fill-current [&_path]:fill-current">
      <IdeozLogo />
    </span>
  );
}

/** Vertical stepper shown in the right panel. Completed steps are blue with a
 * check, the active step is a solid dark dot, upcoming steps are outlined. */
function Stepper({ currentStep }: { currentStep: 1 | 2 | 3 | 4 }): JSX.Element {
  return (
    <ol className="flex flex-col">
      {STEP_META.map((s, idx) => {
        const isDone = s.id < currentStep;
        const isActive = s.id === currentStep;
        const isLast = idx === STEP_META.length - 1;
        return (
          <li key={s.id} className="flex gap-4">
            {/* Rail: dot + connector line */}
            <div className="flex flex-col items-center">
              <span
                className={cn(
                  'flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-sm font-medium',
                  isDone
                    ? 'bg-blue-600 text-white'
                    : isActive
                      ? 'bg-foreground text-background'
                      : 'border border-border text-muted-foreground'
                )}
              >
                {isDone ? <Check size={16} /> : s.id}
              </span>
              {!isLast && (
                <span className={cn('my-1 w-px flex-1', isDone ? 'bg-blue-500' : 'bg-border')} />
              )}
            </div>

            {/* Content */}
            <div className={cn(isLast ? 'pb-0' : 'pb-8')}>
              <h3
                className={cn(
                  'text-[15px] font-medium',
                  isDone
                    ? 'text-blue-600 dark:text-blue-400'
                    : isActive
                      ? 'text-foreground'
                      : 'text-muted-foreground'
                )}
              >
                {s.label}
              </h3>
              <p className="mt-1 max-w-xs text-[13px] leading-relaxed text-muted-foreground">
                {s.desc}
              </p>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

/** "Why this matters" card + the live workspace preview. */
function WhyThisMatters({
  text,
  name,
  industryLabel,
}: {
  text: string;
  name: string;
  industryLabel: string;
}): JSX.Element {
  return (
    <div className="rounded-2xl border border-border bg-card p-6">
      <div className="mb-3 flex items-center gap-2">
        <Sparkles size={14} className="text-amber-500" />
        <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
          Why this matters
        </span>
      </div>
      <p className="text-sm leading-relaxed text-muted-foreground">{text}</p>

      <div className="mt-5 rounded-xl bg-muted/50 p-5">
        <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
          Workspace preview
        </p>
        <p className="mt-2 text-xl font-bold text-foreground">
          {name || 'Your startup'}
          <span className="text-blue-600 dark:text-blue-400">.</span>
        </p>
        <p className="mt-1 text-sm text-muted-foreground">
          {industryLabel || 'Industry not set'}
        </p>
      </div>
    </div>
  );
}

function FieldRow({
  label,
  optional,
  children,
}: {
  label: string;
  optional?: boolean;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="mb-8 space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-foreground">{label}</Label>
        {optional && <span className="text-xs text-muted-foreground">Optional</span>}
      </div>
      {children}
    </div>
  );
}

function FileDropZone({
  files,
  onChange,
  accept: acceptAttr,
  hint,
  icon: Icon = CloudUpload,
}: {
  files: File[];
  onChange: (files: File[]) => void;
  /** Passed to the underlying <input type="file" accept="..."> so the file
   * picker filters by type. Also enforced client-side on drag-and-drop so
   * unsupported files are rejected before upload. */
  accept?: string;
  /** Small helper text rendered under the drop-hint line — used to tell the
   * user why the accepted types matter (tone of voice, screenshots, etc.). */
  hint?: string;
  /** Icon shown in the circular badge (file / image / cloud per step). */
  icon?: LucideIcon;
}): JSX.Element {
  const [isDragOver, setIsDragOver] = useState(false);

  /** Split an accept spec like ".md,.csv,text/csv,image/png" into extension
   * patterns and MIME patterns so we can validate a File against either. */
  const acceptedPatterns = acceptAttr
    ?.split(',')
    .map((p) => p.trim())
    .filter(Boolean);
  const matchesAccept = (file: File): boolean => {
    if (!acceptedPatterns || acceptedPatterns.length === 0) return true;
    return acceptedPatterns.some((pattern) => {
      if (pattern.startsWith('.')) {
        return file.name.toLowerCase().endsWith(pattern.toLowerCase());
      }
      if (pattern.endsWith('/*')) {
        const prefix = pattern.slice(0, -1);
        return file.type.startsWith(prefix);
      }
      return file.type === pattern;
    });
  };

  const accept = (incoming: FileList | File[]) => {
    const accepted: File[] = [];
    for (const f of Array.from(incoming)) {
      if (matchesAccept(f)) accepted.push(f);
      else showToast({ message: `Skipped ${f.name}: unsupported file type.`, severity: 'error' });
    }
    if (accepted.length) onChange([...files, ...accepted]);
  };

  return (
    <div>
      <label
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragOver(true);
        }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setIsDragOver(false);
          if (e.dataTransfer.files?.length) accept(e.dataTransfer.files);
        }}
        className={cn(
          'block cursor-pointer rounded-xl border border-dashed px-6 py-12 text-center transition-colors',
          isDragOver
            ? 'border-blue-400 bg-blue-50/50 dark:border-blue-500 dark:bg-blue-500/10'
            : 'border-border hover:bg-muted/40'
        )}
      >
        <div className="flex flex-col items-center gap-3">
          <span className="flex h-12 w-12 items-center justify-center rounded-full border border-border text-muted-foreground">
            <Icon className="h-5 w-5" size={20} />
          </span>
          <p className="text-sm font-medium text-foreground">Drag and drop or click to attach</p>
          {hint && <p className="max-w-md text-xs text-muted-foreground">{hint}</p>}
        </div>
        <input
          type="file"
          multiple
          hidden
          accept={acceptAttr}
          onChange={(e) => {
            if (e.target.files?.length) accept(e.target.files);
            e.target.value = '';
          }}
        />
      </label>

      {files.length > 0 && (
        <ul className="mt-3 space-y-2">
          {files.map((file, idx) => (
            <li
              key={`${file.name}-${idx}`}
              className="flex items-center gap-3 rounded-lg border border-border bg-muted/40 px-3 py-2"
            >
              <FileIcon className="w-4 h-4 text-muted-foreground shrink-0" />
              <span className="text-sm text-foreground truncate flex-1">{file.name}</span>
              <button
                type="button"
                onClick={() => onChange(files.filter((_, i) => i !== idx))}
                className="text-gray-400 hover:text-red-500 transition-colors"
                aria-label={`Remove ${file.name}`}
              >
                <X className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
